import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  RefreshCw, Save, TestTube2, CheckCircle2, XCircle, ClipboardCheck, AlertCircle, Eye, Plug, Trash2,
} from "lucide-react";
import { Checkbox } from "../components/ui/checkbox";
import LisjakSyncReportDialog from "../components/LisjakSyncReportDialog";

const TYPE_LABEL = {
  member_create: "Nov član",
  member_update: "Posodobitev člana",
  vezica_reassign: "Vezica – prerazporeditev",
  vezica_allocation: "Vezica – manjkajoč član",
};

// Prikaži vse datume v formatu dd.mm.llll (pretvori iz ISO ali drugih formatov)
const fmtDateVal = (v) => {
  if (v === null || v === undefined) return v;
  const s = String(v).trim();
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ].*)?$/);
  if (m) return `${m[3]}.${m[2]}.${m[1]}`;
  m = s.match(/^(\d{1,2})[./-]\s*(\d{1,2})[./-]\s*(\d{4})\.?$/);
  if (m) return `${String(m[1]).padStart(2, "0")}.${String(m[2]).padStart(2, "0")}.${m[3]}`;
  return s;
};

const fmtDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return fmtDateVal(iso);
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

const LisjakPage = () => {
  const { token, hasRole } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const canManage = hasRole(["predsednik", "gospodar", "tajnik", "administrator"]);

  const [config, setConfig] = useState(null);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncReport, setSyncReport] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  // form
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [orgNaziv, setOrgNaziv] = useState("");
  const [enabled, setEnabled] = useState(true);

  // detail dialog
  const [detail, setDetail] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [cfgRes, pendRes] = await Promise.all([
        axios.get(`${API}/lisjak/config`, { headers }),
        axios.get(`${API}/lisjak/pending`, { headers }),
      ]);
      setConfig(cfgRes.data);
      setUsername(cfgRes.data.username || "");
      setOrgNaziv(cfgRes.data.org_naziv || "");
      setEnabled(cfgRes.data.enabled ?? true);
      setPending(pendRes.data.pending || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Napaka pri nalaganju");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, []);

  const testConn = async () => {
    if (!username || !password) { toast.error("Vpiši up.ime in geslo"); return; }
    setTesting(true);
    try {
      const res = await axios.post(
        `${API}/lisjak/test`,
        { username, password, org_naziv: orgNaziv || "X", enabled: true },
        { headers },
      );
      toast.success(`Prijava uspešna: ${res.data.uporabnik_info}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Prijava neuspešna");
    } finally {
      setTesting(false);
    }
  };

  const saveConfig = async () => {
    if (!username || !password || !orgNaziv) {
      toast.error("Izpolni vsa polja"); return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API}/lisjak/config`,
        { username, password, org_naziv: orgNaziv, enabled },
        { headers },
      );
      toast.success("Nastavitve shranjene");
      setPassword("");
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Napaka pri shranjevanju");
    } finally {
      setSaving(false);
    }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/lisjak/sync-now`, {}, { headers });
      setSyncReport(res.data);
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Napaka pri sinhronizaciji");
    } finally {
      setSyncing(false);
    }
  };

  const approveOne = async (id) => {
    try {
      await axios.post(`${API}/lisjak/pending/${id}/approve`, {}, { headers });
      toast.success("Odobreno");
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Napaka");
    }
  };

  const rejectOne = async (id) => {
    if (!window.confirm("Zavrni to spremembo?")) return;
    try {
      await axios.post(`${API}/lisjak/pending/${id}/reject`, {}, { headers });
      toast.success("Zavrnjeno");
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Napaka");
    }
  };

  const approveAll = async () => {
    if (!window.confirm(`Odobri vseh ${pending.length} sprememb?`)) return;
    await runBulk(pending.map((p) => p.id), "approve");
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelected((prev) => (prev.size === pending.length ? new Set() : new Set(pending.map((p) => p.id))));
  };

  const runBulk = async (ids, action) => {
    setBulkLoading(true);
    try {
      const res = await axios.post(`${API}/lisjak/pending/bulk`, { ids, action }, { headers });
      const { ok, errors } = res.data;
      const verb = action === "approve" ? "Odobrenih" : action === "reject" ? "Zavrnjenih" : "Izbrisanih";
      if (errors?.length) {
        toast.warning(`${verb}: ${ok}, napak: ${errors.length}. ${errors[0].label ? errors[0].label + ": " : ""}${errors[0].detail}`);
      } else {
        toast.success(`${verb}: ${ok}`);
      }
      setSelected(new Set());
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Napaka pri množični akciji");
    } finally {
      setBulkLoading(false);
    }
  };

  const bulkAction = async (action) => {
    const ids = [...selected];
    if (ids.length === 0) return;
    const q = action === "approve" ? `Odobri ${ids.length} izbranih sprememb?`
      : action === "reject" ? `Zavrni ${ids.length} izbranih sprememb?`
      : `Izbriši ${ids.length} izbranih sprememb? Zapisi bodo trajno odstranjeni.`;
    if (!window.confirm(q)) return;
    await runBulk(ids, action);
  };

  const statusBadge = () => {
    if (!config?.last_status) return <Badge variant="outline">še ni sinhronizirano</Badge>;
    if (config.last_status === "success") return <Badge className="bg-green-100 text-green-800">uspešno</Badge>;
    if (config.last_status === "auth_error") return <Badge variant="destructive">napačna prijava</Badge>;
    return <Badge variant="destructive">napaka</Badge>;
  };

  if (loading) {
    return <div className="p-8 text-center text-muted-foreground">Nalagam ...</div>;
  }

  return (
    <div className="space-y-6" data-testid="lisjak-page">
      <div>
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <Plug className="h-6 w-6 text-primary" />
          Lisjak – sinhronizacija
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Avtomatsko usklajevanje članov in vezic z uradnim registrom LZS (lisjak.lovska-zveza.si).
          Sinhronizira se vsak večer ob 03:00 CET.
        </p>
      </div>

      {/* Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            Nastavitve dostopa
            {statusBadge()}
          </CardTitle>
          <CardDescription>
            Vodstvo (predsednik/gospodar/tajnik) vnese Lisjak prijavne podatke. Geslo se v bazi shrani šifrirano.
            {config?.uporabnik_info && (
              <div className="text-xs mt-1">Prijavljen kot: <b>{config.uporabnik_info}</b></div>
            )}
            {config?.last_sync_at && (
              <div className="text-xs mt-1">Zadnja sinhronizacija: {fmtDateTime(config.last_sync_at)}</div>
            )}
            {config?.last_error && (
              <div className="text-xs mt-1 text-red-600">Napaka: {config.last_error}</div>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label>Uporabniško ime</Label>
              <Input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="npr. ALEGORK"
                data-testid="lisjak-username"
                disabled={!canManage}
              />
            </div>
            <div>
              <Label>Geslo</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={config?.has_password ? "•••• (že shranjeno) — vnos zamenja" : "geslo"}
                data-testid="lisjak-password"
                disabled={!canManage}
              />
            </div>
            <div>
              <Label>Naziv LD v Lisjaku</Label>
              <Input
                value={orgNaziv}
                onChange={(e) => setOrgNaziv(e.target.value.toUpperCase())}
                placeholder="npr. RAKEK"
                data-testid="lisjak-org"
                disabled={!canManage}
              />
              <p className="text-xs text-muted-foreground mt-1">Točen naziv kot v Lisjaku (velikih črk).</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Switch checked={enabled} onCheckedChange={setEnabled} disabled={!canManage} data-testid="lisjak-enabled" />
            <span className="text-sm">Nočna avtomatska sinhronizacija</span>
          </div>

          <div className="flex flex-wrap gap-2 pt-2">
            <Button variant="outline" onClick={testConn} disabled={testing || !password} data-testid="lisjak-test-btn">
              <TestTube2 className="h-4 w-4 mr-2" />
              {testing ? "Preverjam ..." : "Preveri povezavo"}
            </Button>
            <Button onClick={saveConfig} disabled={saving || !canManage} data-testid="lisjak-save-btn">
              <Save className="h-4 w-4 mr-2" />
              {saving ? "Shranjujem ..." : "Shrani"}
            </Button>
            {config?.has_password && (
              <Button variant="outline" onClick={syncNow} disabled={syncing} data-testid="lisjak-sync-btn">
                <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? "animate-spin" : ""}`} />
                {syncing ? "Sinhroniziram ..." : "Sinhroniziraj zdaj"}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Pending changes */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <ClipboardCheck className="h-5 w-5" />
              Čakajoče spremembe
              {pending.length > 0 && (
                <Badge variant="default" className="ml-2">{pending.length}</Badge>
              )}
            </CardTitle>
            <CardDescription>
              Spremembe iz Lisjaka, ki potrebujejo vaše ročno potrjevanje. Novi člani (z lovsko izkaznico) so bili avtomatsko dodani.
            </CardDescription>
          </div>
          {pending.length > 1 && (
            <Button variant="outline" onClick={approveAll} data-testid="lisjak-approve-all">
              Odobri vse
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {pending.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground text-sm">
              <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-green-500" />
              Ni čakajočih sprememb.
            </div>
          ) : (
            <div className="space-y-2">
              {/* Select-all + bulk action bar */}
              <div className="flex flex-wrap items-center gap-2 border rounded-lg p-2 bg-muted/40">
                <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                  <Checkbox
                    checked={selected.size === pending.length && pending.length > 0}
                    onCheckedChange={toggleSelectAll}
                    data-testid="lisjak-select-all"
                  />
                  Izberi vse
                </label>
                {selected.size > 0 && (
                  <>
                    <Badge variant="secondary" data-testid="lisjak-selected-count">{selected.size} izbranih</Badge>
                    <div className="flex gap-1.5 ml-auto">
                      <Button size="sm" variant="outline" className="text-green-700" onClick={() => bulkAction("approve")} disabled={bulkLoading} data-testid="lisjak-bulk-approve">
                        <CheckCircle2 className="h-3.5 w-3.5 mr-1" />Odobri izbrane
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-600" onClick={() => bulkAction("reject")} disabled={bulkLoading} data-testid="lisjak-bulk-reject">
                        <XCircle className="h-3.5 w-3.5 mr-1" />Zavrni izbrane
                      </Button>
                      <Button size="sm" variant="outline" className="text-slate-600" onClick={() => bulkAction("delete")} disabled={bulkLoading} data-testid="lisjak-bulk-delete">
                        <Trash2 className="h-3.5 w-3.5 mr-1" />Izbriši izbrane
                      </Button>
                    </div>
                  </>
                )}
              </div>
              {pending.map((p) => (
                <div key={p.id} className={`border rounded-lg p-3 flex items-start justify-between gap-3 ${selected.has(p.id) ? "bg-primary/5 border-primary/40" : ""}`} data-testid={`lisjak-pending-${p.id}`}>
                  <Checkbox
                    className="mt-1 flex-shrink-0"
                    checked={selected.has(p.id)}
                    onCheckedChange={() => toggleSelect(p.id)}
                    data-testid={`lisjak-select-${p.id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="outline" className="text-xs">{TYPE_LABEL[p.type] || p.type}</Badge>
                      <span className="text-xs text-muted-foreground">{fmtDateTime(p.created_at)}</span>
                    </div>
                    <div className="font-medium text-sm truncate">{p.entity_label}</div>
                    {p.diff && Object.keys(p.diff).length > 0 && (
                      <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-3 gap-y-1">
                        {Object.entries(p.diff).map(([k, v]) => (
                          <span key={k}>
                            <b>{k}:</b> <span className="line-through text-red-500">{fmtDateVal(String(v.old)) || "—"}</span>
                            {" → "}<span className="text-green-700">{fmtDateVal(String(v.new))}</span>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button size="sm" variant="ghost" onClick={() => setDetail(p)} title="Podrobnosti">
                      <Eye className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="outline" className="text-green-700" onClick={() => approveOne(p.id)} data-testid={`lisjak-approve-${p.id}`}>
                      <CheckCircle2 className="h-3.5 w-3.5 mr-1" />Odobri
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-600" onClick={() => rejectOne(p.id)} data-testid={`lisjak-reject-${p.id}`}>
                      <XCircle className="h-3.5 w-3.5 mr-1" />Zavrni
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{detail?.entity_label}</DialogTitle>
            <DialogDescription>
              {TYPE_LABEL[detail?.type]} · {detail && fmtDateTime(detail.created_at)}
            </DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              {detail.diff && Object.keys(detail.diff).length > 0 && (
                <div>
                  <div className="font-medium mb-1">Spremenjena polja</div>
                  <table className="w-full text-xs">
                    <thead className="text-muted-foreground">
                      <tr><th className="text-left">Polje</th><th className="text-left">Trenutno</th><th className="text-left">Iz Lisjaka</th></tr>
                    </thead>
                    <tbody>
                      {Object.entries(detail.diff).map(([k, v]) => (
                        <tr key={k} className="border-t">
                          <td className="py-1 font-mono">{k}</td>
                          <td className="text-red-600">{fmtDateVal(String(v.old)) || "—"}</td>
                          <td className="text-green-700">{fmtDateVal(String(v.new))}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div>
                <div className="font-medium mb-1">Vsi podatki iz Lisjaka</div>
                <pre className="text-xs bg-muted p-2 rounded max-h-64 overflow-auto">
                  {JSON.stringify(detail.new_data, null, 2)}
                </pre>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetail(null)}>Zapri</Button>
            {detail && (
              <>
                <Button variant="destructive" onClick={() => { rejectOne(detail.id); setDetail(null); }}>Zavrni</Button>
                <Button onClick={() => { approveOne(detail.id); setDetail(null); }}>Odobri</Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <LisjakSyncReportDialog open={!!syncReport} data={syncReport} onClose={() => setSyncReport(null)} />
    </div>
  );
};

export default LisjakPage;
